import { estimateKcal, whoMinutes } from './activity.js'
import { addWeeks, isoWeekKey, startOfWeek } from './date.js'
import type { Activity, CurrentPractice } from './types.js'

/** Recommandation OMS : 150 min d'activité modérée par semaine. */
export const WHO_WEEKLY_MINUTES = 150

/** Objectif proposé par défaut à l'inscription — aligné sur l'OMS. */
export const DEFAULT_WEEKLY_GOAL_MIN = 150

/** Bornes de l'objectif personnel réglable par l'utilisateur. */
export const MIN_WEEKLY_GOAL_MIN = 30
export const MAX_WEEKLY_GOAL_MIN = 600

/**
 * Premier objectif proposé selon la pratique déclarée.
 *
 * Annoncer 150 min à quelqu'un qui ne bouge pas du tout est le meilleur moyen
 * de le perdre : on part de ce qu'il fait déjà, et l'app le rapprochera de la
 * reco OMS progressivement. C'est un choix produit central pour une app qui
 * s'adresse à des gens non motivés.
 */
export function suggestedWeeklyGoal(practice: CurrentPractice): number {
  switch (practice) {
    case 'none':
      return 60
    case 'occasional':
      return 105
    case 'regular':
      return 150
    case 'frequent':
      return 240
  }
}

export interface WeekSummary {
  /** ex. "2026-W40" */
  weekKey: string
  /** Lundi 00:00, ISO 8601 */
  weekStart: string
  /** Toutes les minutes d'activité, quelle que soit l'intensité */
  totalMinutes: number
  /** Minutes pondérées au sens OMS (le léger ne compte pas) */
  whoMinutes: number
  sessions: number
  kcal: number
  /** Vrai pour la semaine en cours, qui n'est pas encore jouée jusqu'au bout */
  isCurrent: boolean
}

export function summarizeWeek(
  activities: readonly Activity[],
  weekStart: Date,
  options: { now?: Date; weightKg?: number } = {},
): WeekSummary {
  const now = options.now ?? new Date()
  const start = startOfWeek(weekStart)
  const end = addWeeks(start, 1)

  let totalMinutes = 0
  let who = 0
  let sessions = 0
  let kcal = 0

  for (const activity of activities) {
    const date = new Date(activity.startedAt)
    if (date < start || date >= end) continue
    totalMinutes += activity.durationMin
    who += whoMinutes(activity)
    kcal += activity.kcal ?? estimateKcal(activity, options.weightKg)
    sessions += 1
  }

  return {
    weekKey: isoWeekKey(start),
    weekStart: start.toISOString(),
    totalMinutes,
    whoMinutes: who,
    sessions,
    kcal,
    isCurrent: isoWeekKey(start) === isoWeekKey(now),
  }
}

/**
 * Les `weeks` dernières semaines, de la plus ancienne à la semaine en cours.
 * Les semaines sans activité sont incluses avec des zéros : une semaine
 * manquée doit exister pour pouvoir casser un streak.
 */
export function weeklySummaries(
  activities: readonly Activity[],
  options: { weeks?: number; now?: Date; weightKg?: number } = {},
): WeekSummary[] {
  const weeks = options.weeks ?? 12
  const now = options.now ?? new Date()
  const current = startOfWeek(now)

  const result: WeekSummary[] = []
  for (let offset = weeks - 1; offset >= 0; offset--) {
    result.push(
      summarizeWeek(activities, addWeeks(current, -offset), {
        now,
        ...(options.weightKg !== undefined ? { weightKg: options.weightKg } : {}),
      }),
    )
  }
  return result
}

/** Avancement 0..1 (plafonné) vers l'objectif personnel. */
export function goalProgress(summary: WeekSummary, weeklyGoalMin: number): number {
  if (weeklyGoalMin <= 0) return 0
  return Math.min(1, summary.totalMinutes / weeklyGoalMin)
}

/** Avancement 0..1 (plafonné) vers la recommandation OMS. */
export function whoProgress(summary: WeekSummary): number {
  return Math.min(1, summary.whoMinutes / WHO_WEEKLY_MINUTES)
}

export function isWeekAchieved(summary: WeekSummary, weeklyGoalMin: number): boolean {
  return summary.totalMinutes >= weeklyGoalMin
}
